import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Scale } from "lucide-react";

const GROUPS = ["all", "chest", "back", "shoulders", "biceps", "triceps", "quads", "hamstrings", "glutes", "calves", "abs", "adductors"];

export default function Exercises() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [unilateralOnly, setUnilateralOnly] = useState(false);

    useEffect(() => {
        api.get("/exercises").then(r => setItems(r.data));
    }, []);

    const filtered = items.filter((i) => {
        if (filter !== "all" && i.muscle_group !== filter) return false;
        if (unilateralOnly && !i.unilateral) return false;
        return true;
    });

    return (
        <div className="space-y-6" data-testid="exercises-page">
            <div>
                <div className="label-eyebrow text-accent-red">Library</div>
                <h1 className="font-display text-4xl font-black uppercase sm:text-5xl">Exercises</h1>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground">★ = preferred primary movement. Others are valid alternatives — pick the one you can progress on for months without pain.</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                {GROUPS.map(g => (
                    <button key={g} onClick={() => setFilter(g)} data-testid={`filter-${g}`} className={`border px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${filter === g ? "border-accent-red bg-accent-red text-white" : "border-border bg-card hover:bg-secondary"}`}>
                        {g}
                    </button>
                ))}
                <div className="mx-2 hidden h-5 w-px bg-border sm:block" />
                <button
                    type="button"
                    onClick={() => setUnilateralOnly((v) => !v)}
                    aria-pressed={unilateralOnly}
                    data-testid="filter-unilateral"
                    title="Show only single-side / single-arm / single-leg exercises"
                    className={`flex items-center gap-1.5 border px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${unilateralOnly ? "border-accent-red bg-accent-red text-white" : "border-border bg-card hover:bg-secondary"}`}
                >
                    <Scale className="h-3.5 w-3.5" />
                    Unilateral only
                </button>
            </div>

            <div className="text-xs text-muted-foreground" data-testid="exercise-count">
                {filtered.length} {filtered.length === 1 ? "exercise" : "exercises"}
                {unilateralOnly && <span className="ml-1">· unilateral only</span>}
                {filter !== "all" && <span className="ml-1">· {filter}</span>}
            </div>

            {filtered.length === 0 ? (
                <div className="tactical-card p-8 text-center text-sm text-muted-foreground" data-testid="exercise-empty-state">
                    No exercises match this filter combination. Clear a filter to see more options.
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {filtered.map(ex => (
                        <Link to={`/exercises/${ex.id}`} key={ex.id} data-testid={`exercise-${ex.id}`} className="block-shadow tactical-card p-4">
                            <div className="flex items-center justify-between label-eyebrow text-muted-foreground">
                                <span>{ex.muscle_group} · {ex.pattern}</span>
                                {ex.unilateral && (
                                    <span className="flex items-center gap-1 border border-accent-red/60 px-1.5 py-0.5 text-[9px] tracking-wider text-accent-red" data-testid={`uni-badge-${ex.id}`}>
                                        <Scale className="h-2.5 w-2.5" /> UNI
                                    </span>
                                )}
                            </div>
                            <div className="mt-1 flex items-center gap-2">
                                {ex.primary && <span className="text-accent-red">★</span>}
                                <h3 className="font-display text-lg font-black uppercase">{ex.name}</h3>
                            </div>
                            {ex.description && <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{ex.description}</p>}
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
